import React, { useState } from "react";
import Modal from "../../components/Modal";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  InventoryHeaderInterface,
  InventoryDetailInterface,
} from "../../interfaces/InventoryInterface";
import { PrimaryButton } from "../../components/Buttons";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import DropdownMenu from "../../components/DropdownMenu";
import { useAppSelector } from "../../store/hooks";
import InventoryHeaderData from "./InventoryHeaderData";
import InventoryDetailsTable from "./InventoryDetailsTable";
import { downloadInventory } from "../../services/inventoryServices";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

interface InventoryModalProps {
  inventory: InventoryHeaderInterface;
  detailData: InventoryDetailInterface[] | null;
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  showPiece: boolean;
}

enum InventoryReportOption {
  DETALLADO,
  AGRUPADO,
}
const reportOptions = [
  {
    title: "DETALLADO",
    value: InventoryReportOption.DETALLADO,
  },
  {
    title: "AGRUPADO",
    value: InventoryReportOption.AGRUPADO,
  }
];

const InventoryDetailModal: React.FC<InventoryModalProps> = ({
  inventory,
  detailData,
  openModal,
  setOpenModal,
  showPiece = false,
}) => {
  const [openPdfPopover, setOpenPdfPopover] = useState(false);
  const [openExcelPopover, setOpenExcelPopover] = useState(false);
  const user = useAppSelector((state) => state.user);
  const handleDownloadClick = async (type: number) => {
    downloadInventory(
          inventory.inventoryHeaderCode as string,
          type, // 1=AGRUPADO. 2=DETALLADO. 3=CUADRE A CERO
          user.user?.userLogin as string,
          openPdfPopover === true ? 1 : 2 // 1=PDF. 2=EXCEL
        );
  };

  const handleDropdownMenuSelect = (
    option: { value: InventoryReportOption },
    type: "pdf" | "excel"
  ) => {
    if (type === "pdf") {
      setOpenExcelPopover(false);
      setOpenPdfPopover(true);
    } else {
      setOpenPdfPopover(false);
      setOpenExcelPopover(true);
    }
    if (option.value === InventoryReportOption.AGRUPADO) {
      handleDownloadClick(1); // 1= AGRUPADO.
    } else 
    if (option.value === InventoryReportOption.DETALLADO) {
      handleDownloadClick(2); // 2= DETALLADO.
    } else {
      handleDownloadClick(3); //3= CUADRE A CERO
    }
  };

  const exportToExcel = async () => {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Reporte de Envíos");
  
      worksheet.columns = [
        { key: "deliveryType", width: 15 },
        { key: "shipmentDate", width: 25 },
        { key: "shipmentNumber", width: 15 },
        { key: "buCodeSource", width: 15 },
        { key: "locationOriginCode", width: 25 },
        { key: "buCodeDestination", width: 15 },
        { key: "locationDestinationCode", width: 15 },
        { key: "serviceName", width: 10 },
        { key: "paymentModeName", width: 20 },
        { key: "chargedWeight", width: 20 },
        { key: "totalPieces", width: 15 },
        { key: "manifestPieces", width: 20 },
        { key: "shipperName", width: 25 },
        { key: "shipperPhone", width: 25 },
        { key: "shipperIdentification", width: 25 },
        { key: "consigneeName", width: 25 },
        { key: "consigneePhone", width: 25 },
        { key: "consigneeIdentification", width: 25 },
        { key: "consigneeAddress", width: 25 },
        { key: "daysActive", width: 25 },
        { key: "daysInStore", width: 25 },
        { key: "observations", width: 25 },
      ];
  
      // Manifest
      const row2 = worksheet.getRow(2);
      row2.getCell(
        1
      ).value = `Inventario ${inventory.inventoryHeaderCode}`;
  
      // Business unit
      const row3 = worksheet.getRow(3);
      row3.getCell(
        1
      ).value = `Tienda ${user.businessUnit?.code} - ${user.businessUnit?.name}`;
  
      // User
      const row4 = worksheet.getRow(4);
      row4.getCell(1).value = `Elaborado por ${user.user?.userName} ${
        user.user?.userLastName
      } (${new Date().toLocaleString()})`;

      console.log(detailData)
  
      // Totals
      const filteredData = detailData;
      row2.getCell(5).value = "Total Guías";
      row3.getCell(5).value = "Total Piezas";
      row4.getCell(5).value = "Total Peso Facturado (kg)";
      row2.getCell(6).value = new Set(filteredData?.filter(x => x.shipmentNumber).map(x => x.shipmentNumber)).size;
      row3.getCell(6).value = filteredData?.length;
      
      row4.getCell(6).value = +(filteredData || [])
        .reduce((acc, item) => acc + (item.chargedWeight ?? 0), 0)
        .toFixed(2);
  
      // Header
      const headerRow = worksheet.getRow(7);
      headerRow.values = [
        "Retiro",
        "Fecha Envío",
        "Guía",
        "Tienda Origen",
        "Ciudad Origen",
        "Tienda Destino",
        "Ciudad Destino",
        "Servicio",
        "Modalidad de Pago",
        "Peso Facturado (kg)",
        "Total Piezas Guía",
        "Piezas en Inventario",
        "Remitente",
        "Teléfono del Remitente",
        "Cédula/RIF del Remitente",
        "Destinatario",
        "Teléfono del Destinatario",
        "Cédula/RIF del Destinatario",
        "Dirección de Entrega",
        "Días Activa",
        "Días en Tienda",
        "Observaciones",
      ];
      headerRow.font = { bold: true };
      headerRow.eachCell((cell) => {
        cell.border = {
          bottom: { style: "thin" },
        };
      });
      Object.values(
        (detailData || []).reduce((acc, item) => {
          const weightToAdd = item.chargedWeight ? Number(item.chargedWeight) : 0;

          if (!acc[item.shipmentNumber]) {
            acc[item.shipmentNumber] = { ...item, chargedWeight: weightToAdd };
          } else {
            const accumulatedWeight = (acc[item.shipmentNumber].chargedWeight || 0) + weightToAdd;

            if (item.pieceNumber > acc[item.shipmentNumber].pieceNumber) {
              acc[item.shipmentNumber] = { ...item, chargedWeight: accumulatedWeight };
            } else {
              acc[item.shipmentNumber].chargedWeight = accumulatedWeight;
            }
          }
          
          return acc;
        }, {} as Record<string, InventoryDetailInterface>)
      ).forEach((item) => {
        const row = worksheet.addRow({
          deliveryType: item.isDelivery ? "Oficina" : "Domicilio",
          shipmentDate: moment(item.creationDateShipment).format("DD/MM/YYYY hh:mm:ss a"),
          shipmentNumber: item.shipmentNumber,
          buCodeSource: item.buCodeSource,
          locationOriginCode: item.locationOriginCode,
          buCodeDestination: item.buCodeConsignee,
          locationDestinationCode: item.locationConsigneeCode,
          serviceName: item.serviceName,
          paymentModeName: item.paymentModeName,
          chargedWeight: (item.chargedWeight || 0).toFixed(2),
          
          totalPieces: item.pieceNumber,
          manifestPieces: item.totalPieces,
          shipperName: item.shipperName,
          shipperPhone: item.shipperPhone,
          shipperIdentification: item.shipperIdentificationNumber,
          consigneeName: item.consigneeName,
          consigneePhone: item.consigneePhone,
          consigneeIdentification: item.consigneeIdentificationNumber,
          consigneeAddress: item.isDelivery ? "En Tienda" : item.locationName,
          daysActive: item.activeLiveShipment?.toString() ?? "",
          daysInStore: item.daysPlatform?.toString() ?? "",
          observations: item.messageValidation ?? "",
        });

        row.getCell(6).numFmt = "0.00";
      });
  
      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `ReporteEnviosInventario_${moment().format("YYYYMMDDHHmmss")}.xlsx`);
    };

  return (
    <Modal
      openModal={openModal}
      setOpenModal={setOpenModal}
      className="bg-gray-200"
    >
      <div className="p-4 flex flex-1 flex-col min-w-[60rem] mb-4">
        {/* Header Title */}
        <div className="flex justify-between">
          <div>
            <span className="font-bold text-xl">Inventario: </span>
            <span className="text-indigo-600 font-bold text-xl">
              {inventory.inventoryHeaderCode}
            </span>
          </div>

          <div className="flex items-center justify-end">
            <button
              type="button"
              className="flex items-center justify-center relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onClick={() => setOpenModal(false)}
            >
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Cerrar panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="w-full mb-1">
          <span className="text-gray-600">Fecha: </span>
          <span className="text-gray-800">
            {!!inventory?.inventoryClosedDate &&
              new Date(inventory.inventoryClosedDate)
                .toLocaleDateString("es-VE", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                .replace("a", "AM")
                .replace("p", "PM")
                .slice(0, -4)}
          </span>
        </div>
      </div>

      {/* linea separador */}
      <hr className="my-1 mx-2 mb-2" />

      {/* Header data */}
      <InventoryHeaderData
        inventoryOpenDate={inventory.inventoryOpenDate}
        inventoryClosedDate={inventory.inventoryClosedDate}
        observation={inventory.observation}
        creationUser={inventory.creationUser}
        updateUser={inventory.updateUser}
      />

      {/* Línea separadora */}
      <hr className="my-2 mx-2" />

      {/* Detalles del inventario */}
      <div className="gap-4 p-2">
        <InventoryDetailsTable data={detailData} showItem={false} showAllStatus={true} 
          showPiece={showPiece}/>
      </div>

      <div className="flex flex-1 flex-row">
        <div className="relative my-4 ml-2">
          <PrimaryButton
            onClick={() => setOpenPdfPopover(!openPdfPopover)}
            className="relative w-28"
          >
            PDF
            {openPdfPopover ? (
              <ChevronUpIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            ) : (
              <ChevronDownIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            )}
          </PrimaryButton>
          <DropdownMenu
            open={openPdfPopover}
            setOpen={setOpenPdfPopover}
            onSelect={(option) => handleDropdownMenuSelect(option, "pdf")}
            options={reportOptions}
            className="!mt-2 w-full transition ease-in-out duration-300"
          />
        </div>

        <div className="relative my-4 ml-2">
          <PrimaryButton
            onClick={() => setOpenExcelPopover(!openExcelPopover)}
            className="relative w-28"
          >
            EXCEL
            {openExcelPopover ? (
              <ChevronUpIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            ) : (
              <ChevronDownIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            )}
          </PrimaryButton>
          <DropdownMenu
            open={openExcelPopover}
            setOpen={setOpenExcelPopover}
            onSelect={(option) => handleDropdownMenuSelect(option, "excel")}
            options={reportOptions}
            className="!mt-2 w-full transition ease-in-out duration-300"
          />

        </div>
        <div className="relative my-4 ml-2"> 
          <PrimaryButton className="w-44" onClick={exportToExcel}>
                    Descargar Reporte
                  </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default InventoryDetailModal;